import React, { useEffect, useState } from 'react';
import i18n from 'i18n-js';
import moment from 'moment';
import Can from 'components/Can';
import Logs from 'components/Logs';
import { useConfirm } from 'hooks';
import Title from 'components/Title';
import Layout from 'components/Layout';
import Comments from 'components/Comments';
import Hidden from '@material-ui/core/Hidden';
import { TasksWidget } from 'components/Tasks';
import { AlarmsWidget } from 'components/Alarms';
import { ExchangeModal } from 'components/Modals';
import { Invoice } from 'components/Reservations';
import useStyles from './SubscriptionDetails.styled';
import { useDispatch, useSelector } from 'react-redux';
import Qualifications from 'components/Qualifications';
import { withRouter, useParams } from 'react-router-dom';
import { PaymentMethods } from 'components/PaymentMethods';
import { Grid, Container, Button } from '@material-ui/core';
import { PeriodsListWidget } from 'components/Subscriptions';
import { VehicleDetailsWidget } from 'components/Vehicles';
import { Details as CustomerDetails } from 'components/Customers';
import { SubscriptionsActions } from 'state/modules/subscriptions';
import { SubscriptionDetails as Details, OptionalItemsWidget } from 'components/Subscriptions';

const SubscriptionDetails = () => {
  const classes = useStyles();
  const { id } = useParams();
  const dispatch = useDispatch();
  const [openExchange, setOpenExchange] = useState(false);
  const [action, setAction] = useState(null);

  const { subscription } = useSelector((state) => state.subscriptions);

  const payment_methods_allowed =
    useSelector((state) => state?.config?.settings?.core?.payment?.payment_methods_allowed) || [];

  const onCancelReturnProcess = () => {
    const payload = {
      exchange_event: 'END',
      action: 'CANCELEXCHANGE',
      action_time: moment(),
    };
    dispatch(SubscriptionsActions.fetchUpdateExchange(id, payload));
  };

  const onEndSubscription = () => {
    const payload = {
      exchange_event: 'END',
      action: 'SCHEDULEEXCHANGE',
      action_time: moment().add('days', 1),
    };
    dispatch(SubscriptionsActions.fetchUpdateExchange(id, payload));
  };

  const [toggleCancelReturnProcess, CancelReturnProcessConfirm] = useConfirm({
    title: i18n.t('subscriptions.cancel_return_process'),
    message: i18n.t('subscriptions.cancel_return_process_msg'),
    cancelText: i18n.t('common.no'),
    confirmText: i18n.t('common.yes'),
    onConfirm: onCancelReturnProcess,
  });

  const [toggleEndSubscription, EndSubscriptionConfirm] = useConfirm({
    title: i18n.t('subscriptions.end_subscription'),
    message: i18n.t('subscriptions.end_subscription_msg'),
    cancelText: i18n.t('common.no'),
    confirmText: i18n.t('common.yes'),
    onConfirm: onEndSubscription,
  });

  useEffect(() => {
    if (id) {
      dispatch(SubscriptionsActions.fetchSubscriptionById(id));
    }
  }, [id]) // eslint-disable-line

  const handleExchangeModalOpen = () => {
    setOpenExchange((prevState) => !prevState);
  };

  const getActions = () => {
    const exchanges = subscription?.exchanges || {};
    const status = subscription?.status || '';

    const actions = [
      {
        action: 'schedule_delivery',
        enabled: ['STARTEXCHANGE'].includes(status),
      },
      {
        action: 'mark_delivered',
        enabled:
          ['STARTEXCHANGE'].includes(status) && exchanges?.START?.exchange_status === 'INPROGRESS',
      },
      {
        action: 'schedule_return',
        enabled: ['INPROGRESS', 'ENDEXCHANGE'].includes(status),
      },
      {
        action: 'mark_returned',
        enabled:
          ['ENDEXCHANGE'].includes(status) && exchanges?.END?.exchange_status === 'INPROGRESS',
      },
      {
        action: 'cancel_return_process',
        enabled: ['ENDEXCHANGE'].includes(status),
      },
      {
        action: 'end_subscription',
        enabled: ['INPROGRESS'].includes(status),
      },
    ];

    return actions.filter((val) => val.enabled);
  };

  const handleAction = (action) => {
    setAction(action);

    switch (action) {
      case 'cancel_return_process':
        toggleCancelReturnProcess();
        break;

      case 'end_subscription':
        toggleEndSubscription();
        break;

      default:
        handleExchangeModalOpen();
        break;
    }
  };

  return (
    <Layout title={i18n.t('subscriptions.details')}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" alignItems="center" justifyContent="space-between">
              <Grid item>
                <Title title={i18n.t('subscriptions.details')} />
              </Grid>
              <Can I="update" on="subscription">
                <Grid item>
                  <Hidden only="xs">
                    {getActions().map((item, index) => (
                      <Button
                        key={index}
                        onClick={() => handleAction(item?.action)}
                        variant={index + 1 === getActions().length ? 'contained' : 'outlined'}
                        style={{
                          textTransform: 'capitalize',
                          marginLeft: 5,
                        }}
                        color="primary"
                      >
                        {i18n.t(`actions.${item?.action}`, { defaultValue: item?.action })}
                      </Button>
                    ))}
                  </Hidden>
                </Grid>
              </Can>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={4}>
                <Grid container spacing={2}>
                  <Can I="read" on="subscription">
                    <Grid item xs={12}>
                      <Details id={id} />
                    </Grid>
                  </Can>
                  <Can I="read" on="vehicle">
                    <Grid item xs={12}>
                      <VehicleDetailsWidget id={subscription?.vehicle?.id} />
                    </Grid>
                  </Can>
                  <Can I="read" a="comment">
                    <Grid item xs={12}>
                      <Comments type="subscription" id={parseInt(id)} />
                    </Grid>
                  </Can>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <Grid container spacing={2}>
                  {id && (
                    <Can I="bypass" on="qualification">
                      <Grid item xs={12}>
                        <Qualifications
                          id={id}
                          type="Subscription"
                          title={i18n.t('subscriptions.quals')}
                        />
                      </Grid>
                    </Can>
                  )}
                  <Can I="read" a="conditional_instance">
                    <Grid item xs={12}>
                      <AlarmsWidget type="subscription" id={parseInt(id)} />
                    </Grid>
                  </Can>
                  <Grid item xs={12}>
                    <PeriodsListWidget customParams={{ subscription_id: parseInt(id) }} />
                  </Grid>
                  {subscription?.invoice_start && (
                    <>
                      <Can I="read" on="invoice">
                        <Grid item xs={12} sm={12}>
                          <Invoice id={subscription?.invoice_start?.id} />
                        </Grid>
                      </Can>
                      {subscription?.invoice_first_period && (
                        <Can I="read" on="invoice">
                          <Grid item xs={12} sm={12}>
                            <Invoice
                              id={subscription?.invoice_first_period?.id}
                              title={i18n.t('subscriptions.invoice_first_period')}
                            />
                          </Grid>
                        </Can>
                      )}
                    </>
                  )}
                  <Grid item xs={12}>
                    <OptionalItemsWidget id={parseInt(id)} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <Grid container spacing={2}>
                  {subscription?.user?.id && (
                    <Can I="bypass" on="qualification">
                      <Grid item xs={12}>
                        <Qualifications
                          title={i18n.t('subscriptions.customer_quals')}
                          id={subscription?.user?.id}
                          type="User"
                        />
                      </Grid>
                    </Can>
                  )}
                  <Can I="read" on="user">
                    <Grid item xs={12}>
                      <CustomerDetails id={parseInt(subscription?.user?.id)} />
                    </Grid>
                  </Can>
                  {Boolean(payment_methods_allowed.length) && (
                    <Can I="read" on="payment_method">
                      <Grid item xs={12}>
                        <PaymentMethods
                          modelId={subscription?.user?.id}
                          accountId={subscription?.user?.account_id}
                        />
                      </Grid>
                    </Can>
                  )}
                  <Can I="read" on="event_log">
                    <Grid item xs={12} sm={12}>
                      <Logs
                        targetId={parseInt(id)}
                        targetType="Subscription"
                        title={i18n.t('subscriptions.logs')}
                        height={439}
                      />
                    </Grid>
                  </Can>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {openExchange && (
          <ExchangeModal
            open={true}
            action={action}
            subscription={subscription}
            handleClick={handleExchangeModalOpen}
          />
        )}
        <CancelReturnProcessConfirm />
        <EndSubscriptionConfirm />
      </Container>
    </Layout>
  );
};

export default withRouter(SubscriptionDetails);
